// =============================================================================
// Create Workspace Page Component
// =============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Code, Database, Globe, Settings, ArrowLeft } from 'lucide-react';
import { workspaceService } from '../services/workspaceService';
import LoadingSpinner from '../components/LoadingSpinner';
import toast from 'react-hot-toast';

const CreateWorkspacePage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('nodejs');
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateId: 'nodejs',
    resources: {
      memory: '1g',
      cpu: '1'
    },
    environment: {},
    settings: {},
    tags: []
  });

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      const templatesData = await workspaceService.getTemplates();
      setTemplates(templatesData);
    } catch (error) {
      console.error('Failed to load templates:', error);
      toast.error('Failed to load workspace templates');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    setFormData(prev => ({
      ...prev,
      templateId
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      toast.error('Workspace name is required');
      return;
    }

    setIsLoading(true);
    try {
      const workspace = await workspaceService.createWorkspace(formData);
      toast.success('Workspace created successfully!');
      navigate(`/workspaces/${workspace.id}`);
    } catch (error) {
      console.error('Failed to create workspace:', error);
      toast.error(error.message || 'Failed to create workspace');
    } finally {
      setIsLoading(false);
    }
  };

  const getTemplateIcon = (templateId) => {
    switch (templateId) {
      case 'nodejs': return '🟢';
      case 'python': return '🐍';
      case 'go': return '🔵';
      case 'rust': return '🦀';
      default: return '📦';
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/dashboard')}
          className="btn btn-ghost btn-sm"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </button>
        <div>
          <h1 className="text-3xl font-bold text-foreground">Create New Workspace</h1>
          <p className="text-muted-foreground mt-1">
            Set up your development environment in seconds
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Information */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Basic Information
            </h2>
          </div>
          <div className="card-content space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Workspace Name *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="My Awesome Project"
                className="input w-full"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Brief description of your project..."
                rows={3}
                className="input w-full resize-none"
              />
            </div>
          </div>
        </div>

        {/* Template Selection */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Code className="w-5 h-5" />
              Choose Template
            </h2>
            <p className="text-sm text-muted-foreground">
              Select a pre-configured development environment
            </p>
          </div>
          <div className="card-content">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {templates.map((template) => (
                <div
                  key={template.id}
                  className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    selectedTemplate === template.id
                      ? 'border-primary bg-primary/5'
                      : 'border-border hover:border-primary/50'
                  }`}
                  onClick={() => handleTemplateSelect(template.id)}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{getTemplateIcon(template.id)}</span>
                    <h3 className="font-medium text-foreground">{template.name}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    {template.description}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {template.languages?.map((lang) => (
                      <span
                        key={lang}
                        className="px-2 py-1 text-xs bg-secondary text-secondary-foreground rounded"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Resource Configuration */}
        <div className="card">
          <div className="card-header">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <Database className="w-5 h-5" />
              Resource Limits
            </h2>
            <p className="text-sm text-muted-foreground">
              Configure CPU and memory limits for your workspace
            </p>
          </div>
          <div className="card-content">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Memory Limit
                </label>
                <select
                  value={formData.resources.memory}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    resources: { ...prev.resources, memory: e.target.value }
                  }))}
                  className="input w-full"
                >
                  <option value="512m">512 MB</option>
                  <option value="1g">1 GB</option>
                  <option value="2g">2 GB</option>
                  <option value="4g">4 GB</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  CPU Limit
                </label>
                <select
                  value={formData.resources.cpu}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    resources: { ...prev.resources, cpu: e.target.value }
                  }))}
                  className="input w-full"
                >
                  <option value="0.5">0.5 CPU</option>
                  <option value="1">1 CPU</option>
                  <option value="2">2 CPU</option>
                  <option value="4">4 CPU</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-6">
          <button
            type="button"
            onClick={() => navigate('/dashboard')}
            className="btn btn-ghost"
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={isLoading || !formData.name.trim()}
            className="btn btn-default"
          >
            {isLoading ? (
              <>
                <LoadingSpinner size="sm" />
                Creating...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                Create Workspace
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateWorkspacePage;
